import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ListResponse, ResponseData } from '../types/axios';
import { MAX_PAGE_SIZE } from './utils';

// The backend caps `length` at 100 (see defaultListOptions.ts) so list endpoints
// can't return everything in one shot. The first request reports recordsTotal;
// any remaining pages are fetched concurrently and concatenated in order.
export async function fetchAllPages<T>(
  axios: AxiosInstance,
  url: string,
  config: AxiosRequestConfig = {},
): Promise<T[]> {
  const fetchPage = async (start: number): Promise<ListResponse<T>> => {
    const { data: response } = await axios.get<ResponseData<ListResponse<T>>>(url, {
      ...config,
      params: { ...(config.params || {}), start, length: MAX_PAGE_SIZE },
    });
    return response?.data;
  };

  const firstPage = await fetchPage(0);
  if (!firstPage?.data?.length) {
    return [];
  }

  const total = firstPage.recordsTotal ?? firstPage.data.length;
  if (total <= MAX_PAGE_SIZE) {
    return firstPage.data;
  }

  const remainingStarts: number[] = [];
  for (let start = MAX_PAGE_SIZE; start < total; start += MAX_PAGE_SIZE) {
    remainingStarts.push(start);
  }

  const restPages = await Promise.all(remainingStarts.map(fetchPage));
  return [
    ...firstPage.data,
    ...restPages.flatMap((p) => p?.data ?? []),
  ];
}
